import React from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";

import { AuthProvider, useAuth } from "@/context/AuthContext";
import { LangProvider } from "@/context/LangContext";
import { IllustrationsProvider } from "@/context/IllustrationsContext";
import { GateProvider, useGate } from "@/context/GateContext";
import GateScreen from "@/pages/GateScreen";

import Landing from "@/pages/Landing";
import Magazine from "@/pages/Magazine";
import Games from "@/pages/Games";
import Stories from "@/pages/Stories";
import Comics from "@/pages/Comics";
import ComicReader from "@/pages/ComicReader";
import Biblioteca from "@/pages/Biblioteca";
import ExamsInfo from "@/pages/ExamsInfo";
import ExamPublic from "@/pages/ExamPublic";
import Ranking from "@/pages/Ranking";
import Onboarding from "@/pages/Onboarding";
import OnboardingRole from "@/pages/OnboardingRole";
import Certificate from "@/pages/Certificate";
import AuthCallback from "@/pages/AuthCallback";

import AdminGuard from "@/pages/admin/AdminGuard";
import AdminDashboard from "@/pages/admin/Dashboard";
import AdminExams from "@/pages/admin/Exams";
import AdminResponses from "@/pages/admin/Responses";
import AdminEmployees from "@/pages/admin/Employees";
import AdminQuestions from "@/pages/admin/Questions";
import AdminIllustrations from "@/pages/admin/Illustrations";
import AdminAIGenerator from "@/pages/admin/AIGenerator";
import AdminCertificates from "@/pages/admin/Certificates";
import AdminRoles from "@/pages/admin/Roles";
import AdminSettings from "@/pages/admin/Settings";
import AdminStats from "@/pages/admin/Stats";
import AdminComicIllustrations from "@/pages/admin/ComicIllustrations";
import AdminComicContent from "@/pages/admin/ComicContentEditor";
import AdminCertificateDesigner from "@/pages/admin/CertificateDesigner";
import AdminStaff from "@/pages/admin/Staff";
import AdminPermissions from "@/pages/admin/Permissions";
import AdminTeamRanking from "@/pages/admin/TeamRanking";
import AdminDijeron from "@/pages/admin/DijeronAdmin";
import TeamDuelAdmin from "@/pages/admin/TeamDuelAdmin";
import CanalesDijeronPreview from "@/pages/CanalesDijeronPreview";
import CanalesDijeron from "@/pages/CanalesDijeron";
import CanalesDijeronDuelo from "@/pages/CanalesDijeronDuelo";
import TeamDuelPlayer from "@/pages/TeamDuelPlayer";
import TeamDuelBoard from "@/pages/TeamDuelBoard";
import StaffLogin from "@/pages/StaffLogin";
import ChangePassword from "@/pages/ChangePassword";
import InviteActivate from "@/pages/InviteActivate";
import ManagerTeam from "@/pages/ManagerTeam";
import InstallPWA from "@/components/InstallPWA";

function AppRouter() {
  const location = useLocation();
  const gate = useGate();
  const auth = useAuth();

  // Process Emergent auth callback synchronously (URL fragment, reactive via useLocation)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  // Public share URLs bypass the app gate — anyone with an exam, role or
  // comic link can open it without knowing the org passcode. `/login` and
  // `/change-password` also bypass so the new role-picker → email/password
  // flow works even when the app hasn't been unlocked yet.
  const pathname = location.pathname || "";
  const isPublicShareUrl =
    pathname.startsWith("/exam/") ||
    pathname.startsWith("/onboarding/") ||
    pathname.startsWith("/comics") ||
    pathname.startsWith("/biblioteca") ||
    pathname.startsWith("/invite/") ||
    pathname === "/juego/canales-dijeron-preview" ||
    pathname === "/juego/canales-dijeron" ||
    pathname === "/juego/canales-dijeron/duelo" ||
    pathname.startsWith("/juego/canales-dijeron/duelo/") ||
    pathname.startsWith("/juego/canales-dijeron/") ||
    pathname.startsWith("/juego/duelo-equipos/") ||
    pathname === "/login" ||
    pathname === "/change-password";

  // App-wide access control: every non-share URL requires (a) the gate to
  // be unlocked AND (b) an authenticated user. Without the auth check the
  // Landing page would be reachable through a stale gate_token cookie —
  // the user explicitly asked for "always require email + password".
  if (!isPublicShareUrl) {
    if (gate?.state === "loading" || auth?.loading) {
      return (
        <div className="min-h-screen flex items-center justify-center font-display bg-canales-cream">
          <div className="animate-pulse">Cargando…</div>
        </div>
      );
    }
    if (gate?.state === "locked" || !auth?.user) {
      return <GateScreen />;
    }
  }

  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/magazine" element={<Magazine />} />
      <Route path="/games" element={<Games />} />
      <Route path="/stories" element={<Stories />} />
      <Route path="/comics" element={<Comics />} />
      <Route path="/comics/:slug" element={<ComicReader />} />
      <Route path="/biblioteca" element={<Biblioteca />} />
      <Route path="/exams-info" element={<ExamsInfo />} />
      <Route path="/exam/:slug" element={<ExamPublic />} />
      <Route path="/ranking" element={<Ranking />} />
      <Route path="/onboarding" element={<Onboarding />} />
      <Route path="/onboarding/:slug" element={<OnboardingRole />} />
      <Route path="/juego/canales-dijeron-preview" element={<CanalesDijeronPreview />} />
      <Route path="/juego/canales-dijeron" element={<CanalesDijeron />} />
      <Route path="/juego/canales-dijeron/duelo" element={<CanalesDijeronDuelo />} />
      <Route path="/juego/canales-dijeron/duelo/:code" element={<CanalesDijeronDuelo />} />
      <Route path="/juego/canales-dijeron/:slug" element={<CanalesDijeron />} />

      <Route path="/juego/duelo-equipos/:code" element={<TeamDuelPlayer />} />
      <Route path="/juego/duelo-equipos/:code/tv" element={<TeamDuelBoard />} />
      <Route path="/juego/duelo-equipos/:code/espectador" element={<TeamDuelBoard />} />

      <Route
        path="/admin"
        element={
          <AdminGuard>
            <AdminDashboard />
          </AdminGuard>
        }
      />
      <Route
        path="/admin/exams"
        element={
          <AdminGuard>
            <AdminExams />
          </AdminGuard>
        }
      />
      <Route
        path="/admin/responses"
        element={
          <AdminGuard>
            <AdminResponses />
          </AdminGuard>
        }
      />
      <Route
        path="/admin/employees"
        element={
          <AdminGuard>
            <AdminEmployees />
          </AdminGuard>
        }
      />
      <Route
        path="/admin/questions"
        element={
          <AdminGuard>
            <AdminQuestions />
          </AdminGuard>
        }
      />
      <Route
        path="/admin/illustrations"
        element={
          <AdminGuard>
            <AdminIllustrations />
          </AdminGuard>
        }
      />
      <Route
        path="/admin/ai-generator"
        element={
          <AdminGuard>
            <AdminAIGenerator />
          </AdminGuard>
        }
      />
      <Route
        path="/admin/certificates"
        element={
          <AdminGuard>
            <AdminCertificates />
          </AdminGuard>
        }
      />
      <Route
        path="/admin/certificate/:name"
        element={
          <AdminGuard>
            <Certificate />
          </AdminGuard>
        }
      />
      <Route
        path="/admin/roles"
        element={
          <AdminGuard>
            <AdminRoles />
          </AdminGuard>
        }
      />
      <Route
        path="/admin/settings"
        element={
          <AdminGuard>
            <AdminSettings />
          </AdminGuard>
        }
      />
      <Route
        path="/admin/stats"
        element={
          <AdminGuard>
            <AdminStats />
          </AdminGuard>
        }
      />
      <Route
        path="/admin/comic-illustrations"
        element={
          <AdminGuard>
            <AdminComicIllustrations />
          </AdminGuard>
        }
      />
      <Route
        path="/admin/comic-content"
        element={
          <AdminGuard>
            <AdminComicContent />
          </AdminGuard>
        }
      />
      <Route
        path="/admin/certificate-designer"
        element={
          <AdminGuard>
            <AdminCertificateDesigner />
          </AdminGuard>
        }
      />
      <Route
        path="/admin/staff"
        element={
          <AdminGuard>
            <AdminStaff />
          </AdminGuard>
        }
      />
      <Route
        path="/admin/permissions"
        element={
          <AdminGuard>
            <AdminPermissions />
          </AdminGuard>
        }
      />
      <Route
        path="/admin/team-ranking"
        element={
          <AdminGuard>
            <AdminTeamRanking />
          </AdminGuard>
        }
      />
      <Route
        path="/admin/dijeron"
        element={
          <AdminGuard>
            <AdminDijeron />
          </AdminGuard>
        }
      />
      <Route
        path="/admin/duelo-equipos"
        element={
          <AdminGuard>
            <TeamDuelAdmin />
          </AdminGuard>
        }
      />
      <Route path="/login" element={<StaffLogin />} />
      <Route path="/change-password" element={<ChangePassword />} />
      <Route path="/invite/:token" element={<InviteActivate />} />
      <Route path="/manager/team" element={<ManagerTeam />} />
    </Routes>
  );
}

export default function App() {
  return (
    <LangProvider>
      <GateProvider>
        <AuthProvider>
          <IllustrationsProvider>
            <BrowserRouter>
              <AppRouter />
              <InstallPWA />
              <Toaster position="top-center" richColors />
            </BrowserRouter>
          </IllustrationsProvider>
        </AuthProvider>
      </GateProvider>
    </LangProvider>
  );
}
